import { ApiService } from 'src/app/services/api/api.service';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SignupService } from 'src/app/services/signup/signup.service';
import Utils from 'src/app/helpers/utils';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ToastService } from 'src/app/services/toast/toast.service';

type SendedValues = {
  id?: number;
  referent: string;
  slugs: string[];
};
@Component({
  selector: 'app-signup-unified-person-attachments',
  templateUrl: './signup-unified-person-attachments.component.html',
  styleUrls: ['./signup-unified-person-attachments.component.scss'],
})
export class SignupUnifiedPersonAttachmentsComponent implements OnInit {
  _attachments: Attachment[] = [];

  _representativeAttachments: RepresentativeAttachment[] = [];

  get attachments() {
    return this._attachments;
  }

  set attachments(value) {
    this._attachments = value;
  }

  get representativeAttachments() {
    return this._representativeAttachments;
  }

  set representativeAttachments(value) {
    this._representativeAttachments = value;
  }

  today = Utils.todayString();

  loading = true;
  loadingContent = false;
  fullScreenLoading = false; // Spinner de tela cheia para shareholder

  label = 'documento do cadastrado';

  userType: string;

  representatives: RepresentativeResponse[] = [];
  files: Files[] = [];

  sending = false;

  // Carbon Warning Modal properties
  showDeleteModal = false;
  fileToDelete: Files | null = null;

  isShareholder = false;
  isExempt = false; // Armazena se o usuário está dispensado por norma

  constructor(
    public signupService: SignupService,
    private authService: AuthService,
    private api: ApiService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    // Verificar se é shareholder pela URL
    this.isShareholder = this.router.url.includes('shareholder');
    
    // Sempre busca os required files, independente de framework_choices
    await this.getRequiredFiles();

    if (
      this.signupService.signupChoices.framework_choices &&
      this.signupService.signupChoices.framework_choices.length > 0
    ) {
      await this.getData();
    }

    this.signupService.choicesFetchedEvent.subscribe(() => {
      this.getData();
    });
  }

  async getData() {
    this.loading = true;
    // Se for shareholder, ativar spinner de tela cheia
    if (this.isShareholder) {
      this.fullScreenLoading = true;
    }
    
    // Busca files, representatives e required files
    await Promise.all([
      this.getFiles(),
      this.getRepresentatives(),
      this.getRequiredFiles(),
    ]);
    
    // Se for shareholder, verifica se está dispensado por norma
    if (this.isShareholder) {
      await this.checkInvestorProfileExemption();
    }
    
    this.setAttachments();
  }

  async getRequiredFiles() {
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        return;
      }

      const { data } = await this.api.get<any>({
        route: `api/required-files/${routeId}`,
        token: true,
      });

      // Define o tipo do usuário baseado no register.type retornado pela API
      if (data?.register?.type) {
        this.userType = data.register.type; // 'PF' ou 'PJ'
      } else {
        // Fallback: usa o tipo do usuário logado
        this.userType = this.authService.getUser().type;
      }

      // Armazena os required_files para uso em setAttachments()
      if (data?.required_files) {
        // Armazena temporariamente no signupService para manter compatibilidade
        if (!this.signupService.signupData) {
          this.signupService.signupData = {};
        }
        this.signupService.signupData.requested_files = data.required_files;
      }
    } catch (error) {
      console.error('Erro ao buscar required files:', error);
      // Em caso de erro, usa o tipo do usuário logado como fallback
      this.userType = this.authService.getUser().type;
    }
  }

  // Remove reference from object
  removeReference(object: any) {
    const string = JSON.stringify(object);
    const newObj = JSON.parse(string);

    return newObj;
  }

  async checkInvestorProfileExemption() {
    try {
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        this.isExempt = false;
        return;
      }

      const { data } = await this.api.get({
        route: 'api/registration/investor-profile-pf/',
        params: { register_uuid: routeId },
        token: true,
      });

      let profileData = null;

      // Se data é um array, pegar o item com o maior id
      if (Array.isArray(data) && data.length > 0) {
        profileData = data.reduce((prev, current) => {
          return prev.id > current.id ? prev : current;
        });
      }
      // Se data é um objeto válido, usar diretamente
      else if (
        data &&
        !Array.isArray(data) &&
        typeof data === 'object' &&
        Object.keys(data).length > 0
      ) {
        profileData = data;
      }

      // Define isExempt baseado no profileData
      this.isExempt = profileData?.is_exempt || false;
    } catch (error) {
      console.error('Erro ao verificar dispensa por norma:', error);
      this.isExempt = false;
    }
  }

  async setAttachments() {
    if (this.signupService.signupData?.requested_files) {
      let slugsToFilter: string[] = [
        'procuracao-representante',
        'comprovacao-aplicacoes-10m',
        'comprovacao-aplicacoes-1m',
        'plano-recuperacao',
      ];

      const slugsToUnfilter: string[] = [];

      if (this.signupService.complementaryData) {
        if (this.representatives.length > 0) {
          slugsToUnfilter.push('procuracao-representante');
        }

        if (
          this.signupService.complementaryData?.investor_status
            ?.has_investments_bigger_then_one_million
        ) {
          slugsToUnfilter.push('comprovacao-aplicacoes-1m');
        }

        if (
          this.signupService.complementaryData?.investor_status
            ?.has_investments_bigger_then_ten_million
        ) {
          slugsToUnfilter.push('comprovacao-aplicacoes-10m');
        }

        if (this.signupService.complementaryData?.general?.judicial_recovery) {
          slugsToUnfilter.push('plano-recuperacao');
        }
      }

      slugsToFilter = slugsToFilter.filter(
        (slug) => !slugsToUnfilter.includes(slug)
      );

      // A nova API já retorna os documentos filtrados por tipo e role,
      // mas ainda aplicamos os filtros condicionais por slugs
      let newRequestedFiles = [
        ...this.signupService.signupData.requested_files,
      ];

      // Aplica filtros condicionais por slugs
      newRequestedFiles = newRequestedFiles.filter(
        (_attachment) => !slugsToFilter.includes(_attachment.slug)
      );

      // Se for shareholder, trata o item de dispensa por norma
      if (this.isShareholder) {
        // Procura o item que contém 'dispensa' no slug
        const dispenseIndex = newRequestedFiles.findIndex((file: any) =>
          file.slug?.toLowerCase().includes('dispensa')
        );

        if (dispenseIndex !== -1) {
          if (this.isExempt) {
            // Se está dispensado, torna obrigatório
            newRequestedFiles[dispenseIndex].required = true;
          } else {
            // Se não está dispensado, remove da lista
            newRequestedFiles.splice(dispenseIndex, 1);
          }
        }
      }

      // Mapeia os documentos para o formato esperado
      this.attachments = newRequestedFiles.map((file: any) => ({
        id: file.id,
        name: file.name,
        description: file.description,
        slug: file.slug,
        files: [],
        required: file.required,
        date_due: '',
        end_date_due: '',
        have_due_date: file.have_due_date || false,
        isFromInvalid: false,
        isToInvalid: false,
        minDate: this.today,
        individual: file.individual || false,
      }));

      const newAttachments = this.attachments.filter((attachment) => {
        return attachment.individual;
      });

      const newRepresentativeAttachments = this.representatives.map(
        (representative) => {
          let info = representative.info;
          if (typeof info === 'string') {
            try {
              info = JSON.parse(info);
            } catch (e) {
              console.error('Error parsing representative info', e);
              info = {};
            }
          }

          const infoCompany = info?.company || info?.register?.company;
          const companyFromRegister = representative.representative_register
            ?.company as any;

          const displayName =
            infoCompany?.corporate_name ||
            companyFromRegister?.corporate_name ||
            infoCompany?.representative_name ||
            companyFromRegister?.representative_name ||
            representative.full_name ||
            representative.representative_register?.person?.full_name ||
            'Representante';

          return {
            representative: {
              id: representative.representative_register.id,
              name: displayName,
            },
            attachments: [...newAttachments],
          };
        }
      );

      this.representativeAttachments = this.removeReference(
        newRepresentativeAttachments
      );

      await this.clearInvalidFiles();

      this.signupService.setPersonFormStatus(
        'attachments',
        this.validateSendedFiles()
      );
    }

    this.loading = false;
    // Se for shareholder, desativar spinner de tela cheia
    if (this.isShareholder) {
      this.fullScreenLoading = false;
    }
  }

  async clearInvalidFiles() {
    // Pega o register_uuid da rota (obrigatório, sem fallback)
    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId) {
      console.error('Register UUID não encontrado na URL');
      return;
    }

    const ids = this.representatives.map(
      (representative) => representative.representative_register.id
    );

    const invalidFiles = this.files.filter(
      (file) =>
        file.referent === 'representative' && !ids.includes(file.referent_id)
    );

    if (invalidFiles.length > 0) {
      const promises = invalidFiles.map((file) =>
        this.api.delete<ApiResponse<Files>>({
          route: `api/registration/files-data/${file.id}/`,
          params: { register_uuid: routeId },
          token: true,
        })
      );

      await Promise.all(promises);
    }
  }

  async setRepresentativeFiles(
    index: number,
    fileIndex: number,
    files: FilePicked[]
  ) {
    const newRepresentativeAttachments = [...this.representativeAttachments];

    newRepresentativeAttachments[index].attachments[fileIndex].files = [
      ...files,
    ];

    newRepresentativeAttachments[index].attachments[fileIndex].user =
      newRepresentativeAttachments[index].representative.id;

    this.representativeAttachments = newRepresentativeAttachments;

    if (
      newRepresentativeAttachments[index].attachments[fileIndex].files.length >
      0
    ) {
      if (
        newRepresentativeAttachments[index].attachments[fileIndex].have_due_date
      ) {
        this.dateValidator(fileIndex, index);
      }

      // Upload automático quando arquivo for anexado
      await this.uploadRepresentativeAttachment(
        newRepresentativeAttachments[index],
        fileIndex
      );
    } else {
      if (
        !newRepresentativeAttachments[index].attachments[fileIndex]
          .have_due_date
      ) {
        newRepresentativeAttachments[index].attachments[
          fileIndex
        ].isFromInvalid = false;
        newRepresentativeAttachments[index].attachments[fileIndex].isToInvalid =
          false;
      }
    }
  }

  trackRepresentativeAttachment(
    index: number,
    item: RepresentativeAttachment
  ) {
    return item?.representative?.id || index;
  }

  async setFiles(index: number, files: FilePicked[]) {
    const newAttachments = [...this.attachments];
    newAttachments[index].files = [...files];

    this.attachments = newAttachments;

    if (newAttachments[index].files.length > 0) {
      if (newAttachments[index].have_due_date) {
        this.dateValidator(index);
      }

      // Upload automático quando arquivo for anexado
      await this.uploadAttachment(newAttachments[index]);
    } else {
      if (!newAttachments[index].have_due_date) {
        newAttachments[index].isFromInvalid = false;
        newAttachments[index].isToInvalid = false;
      }
    }
  }

  handleDateChange(
    value: string,
    input: string,
    index: number,
    representativeIndex?: number
  ) {
    const newAttachments =
      representativeIndex !== undefined
        ? [...this.representativeAttachments[representativeIndex].attachments]
        : [...this.attachments];

    if (this.validateDateValid(value)) {
      if (newAttachments[index]) {
        const formatedDate = value.split('/').reverse().join('-');
        if (input === 'from') {
          newAttachments[index].date_due = formatedDate;
          newAttachments[index].minDate =
            formatedDate > this.today ? formatedDate : this.today;
        } else {
          newAttachments[index].end_date_due = formatedDate;
        }
      }

      if (representativeIndex !== undefined) {
        this.representativeAttachments[representativeIndex].attachments =
          newAttachments;
        this.dateValidator(index, representativeIndex);
      } else {
        this.attachments = newAttachments;
        this.dateValidator(index);
      }
    } else {
      if (input === 'from') {
        newAttachments[index].isFromInvalid = true;
      } else {
        newAttachments[index].isToInvalid = true;
      }
    }
  }

  dateValidator(index: number, representativeIndex?: number) {
    const attachment =
      representativeIndex !== undefined
        ? this.representativeAttachments[representativeIndex].attachments[index]
        : this.attachments[index];

    if (attachment) {
      const fromDate = attachment.date_due;
      const toDate = attachment.end_date_due;

      const fromYear = fromDate ? fromDate.split('-')[0] : '';
      const toYear = toDate ? toDate.split('-')[0] : '';

      // Limpa os tipos de erro anteriores
      (attachment as any).fromErrorType = null;
      (attachment as any).toErrorType = null;

      // Validações para data "De"
      let fromErrorType: string | null = null;
      if (fromDate) {
        if (fromYear.length !== 4) {
          fromErrorType = 'invalid_year';
        } else if (new Date(fromDate) > new Date()) {
          fromErrorType = 'future_date';
        } else if (Number(fromYear) < new Date().getFullYear() - 100) {
          fromErrorType = 'too_old';
        }
      }

      // Validações para data "Até"
      let toErrorType: string | null = null;
      if (toDate) {
        if (toYear.length !== 4) {
          toErrorType = 'invalid_year';
        } else if (new Date(toDate) <= new Date()) {
          toErrorType = 'not_future';
        } else if (Number(toYear) >= new Date().getFullYear() + 100) {
          toErrorType = 'too_future';
        } else if (fromDate && new Date(fromDate) > new Date(toDate)) {
          toErrorType = 'before_from';
        }
      }

      if (!attachment.required && !fromDate) {
        attachment.isFromInvalid = false;
        attachment.isToInvalid = false;
        (attachment as any).fromErrorType = null;
        (attachment as any).toErrorType = null;
        return;
      }

      if (fromDate && !toDate) {
        attachment.isFromInvalid = !!fromErrorType;
        attachment.isToInvalid = true;
        (attachment as any).fromErrorType = fromErrorType;
        (attachment as any).toErrorType = 'required';
        return;
      }

      if (toDate && !fromDate) {
        attachment.isFromInvalid = true;
        attachment.isToInvalid = !!toErrorType;
        (attachment as any).fromErrorType = 'required';
        (attachment as any).toErrorType = toErrorType;
        return;
      }

      if (fromDate && toDate) {
        attachment.isFromInvalid = !!fromErrorType;
        attachment.isToInvalid = !!toErrorType;
        (attachment as any).fromErrorType = fromErrorType;
        (attachment as any).toErrorType = toErrorType;
        return;
      }
    }

    attachment.isFromInvalid = true;
    attachment.isToInvalid = true;
    (attachment as any).fromErrorType = 'required';
    (attachment as any).toErrorType = 'required';
  }

  validateDateValid(value: string) {
    if (value && value.length == 10) {
      const date = value.split('/');

      if (date.length != 3) {
        return false;
      }

      const day = date[0];
      const month = date[1];
      const year = date[2];

      if (day && month && year) {
        const date = new Date(Number(year), Number(month) - 1, Number(day));
        const _day = String(date.getDate()).padStart(2, '0');

        if (
          String(date.getFullYear()) != year ||
          date.getMonth() != Number(month) - 1 ||
          _day != day
        ) {
          return false;
        }

        if (date.toString() == 'Invalid Date') {
          return false;
        }

        return true;
      }
    }

    return false;
  }

  getFromDateErrorMessage(attachment: Attachment): string {
    if (!attachment.isFromInvalid) {
      return '';
    }

    const errorType = (attachment as any).fromErrorType;
    
    if (errorType === 'invalid_year') {
      return 'O ano deve ter 4 dígitos';
    }
    if (errorType === 'future_date') {
      return 'A data não pode ser futura';
    }
    if (errorType === 'too_old') {
      return 'A data não pode ser anterior a 100 anos';
    }
    if (errorType === 'required') {
      return 'Campo obrigatório';
    }
    
    return 'Data inválida';
  }

  getToDateErrorMessage(attachment: Attachment): string {
    if (!attachment.isToInvalid) {
      return '';
    }

    const errorType = (attachment as any).toErrorType;
    
    if (errorType === 'invalid_year') {
      return 'O ano deve ter 4 dígitos';
    }
    if (errorType === 'not_future') {
      return 'A data deve ser maior que a data atual';
    }
    if (errorType === 'too_future') {
      return 'A data não pode ultrapassar 100 anos no futuro';
    }
    if (errorType === 'before_from') {
      return 'A data "Até" deve ser maior que a data "De"';
    }
    if (errorType === 'required') {
      return 'Campo obrigatório';
    }
    
    return 'Data inválida';
  }

  renderLabel(user: any, isRepresentative: boolean) {
    if (isRepresentative) {
      return `Documentos de ${user.representative.name}`;
    } else {
      return `Documentos ${
        this.userType === 'PJ' ? 'da empresa' : 'do titular'
      }`;
    }
  }

  async getRepresentatives() {
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        return;
      }

      const { data } = await this.api.get<
        ApiResponse<RepresentativeResponse[]>
      >({
        route: 'api/registration/representatives-data/',
        params: { register_uuid: routeId },
        token: true,
      });

      this.representatives = data;
    } catch (error) {
      if (error.status !== 404) {
        this.toast.show('error', 'Erro', error.error.message);
      }
    }
  }

  async getFiles() {
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        console.error('Register UUID não encontrado na URL');
        return;
      }

      const { data } = await this.api.get<ApiResponse<Files[]>>({
        route: 'api/registration/files-data/',
        params: { register_uuid: routeId },
        token: true,
      });

      this.files = data;
    } catch (error) {
      if (error.status !== 404) {
        this.toast.show('error', 'Erro', error.error.message);
      }
    }
  }

  //   getInvalid() {
  //     const invalid = this.attachments.some((_attachment) => {
  //       if (_attachment.required) {
  //         if (_attachment.files.length === 0) {
  //           return true;
  //         } else {
  //           if (_attachment.have_due_date) {
  //             if (!(!!_attachment.date_due && !!_attachment.end_date_due)) {
  //               return true;
  //             }
  //           }
  //         }
  //       }

  //       return false;
  //     });

  //     const representativesInvalid = this.representativeAttachments.map(
  //       (_representative) => {
  //         const isValid = _representative.attachments.some((_attachment) => {
  //           if (_attachment.required) {
  //             if (_attachment.files.length === 0) {
  //               return true;
  //             } else {
  //               if (_attachment.have_due_date) {
  //                 if (!(!!_attachment.date_due && !!_attachment.end_date_due)) {
  //                   return true;
  //                 }
  //               }
  //             }
  //           }

  //           return false;
  //         });

  //         return isValid;
  //       }
  //     );

  //     const validArr = [...representativesInvalid, invalid];

  //     return validArr.some((item) => item);
  //   }

  getSendedFiles(id) {
    const files = this.files.filter(
      (file) =>
        file.referent === 'register' &&
        file.type !== null &&
        file.type.id === id
    );

    return files;
  }

  getRepresentativeSendedFiles(id, representativeId) {
    const files = this.files.filter(
      (file) =>
        file.referent === 'representative' &&
        file.type !== null &&
        file.type.id === id &&
        file.referent_id === representativeId
    );

    return files;
  }

  openFile(file: string) {
    window.open(file, '_blank');
  }

  getFileIcon(file: string) {
    if (!file) return '';
    
    // Verifica se é uma imagem (png, jpg, jpeg)
    const isImage = /\.(png|jpg|jpeg)$/i.test(file);
    
    if (file.includes('.pdf')) {
      return 'assets/images/pdf.png';
    } else if (file.includes('.doc') || file.includes('.docx')) {
      return 'assets/images/word.png';
    } else if (file.includes('.xls') || file.includes('.xlsx')) {
      return 'assets/images/xls.png';
    } else if (file.includes('.ppt') || file.includes('.pptx')) {
      return 'assets/images/powerpoint.png';
    } else if (isImage) {
      // Para imagens, retorna a URL completa
      // Se já começa com http, retorna como está
      if (file.startsWith('http://') || file.startsWith('https://')) {
        return file;
      }
      // Caso contrário, concatena com a URL da API
      return this.api.url + file;
    } else {
      return file;
    }
  }

  getFileName(file: string) {
    if (!file) return '';

    try {
      // Remove query parameters
      const urlWithoutParams = file.split('?')[0];

      // Extrai o nome do arquivo da URL
      // Padrão: .../uploads/YYYY/MM/DD/nome_do_arquivo.ext
      const regex = /uploads\/\d{4}\/\d{2}\/\d{2}\/(.+)$/;
      const match = urlWithoutParams.match(regex);

      if (match && match[1]) {
        return decodeURIComponent(match[1]);
      }

      // Fallback: pega o último segmento da URL
      const segments = urlWithoutParams.split('/');
      return segments[segments.length - 1] || '';
    } catch (error) {
      return '';
    }
  }

  openDeleteModal(file: Files) {
    this.fileToDelete = file;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.fileToDelete = null;
  }

  async confirmDeleteFile() {
    if (!this.fileToDelete) return;

    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        this.toast.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota'
        );
        return;
      }

      await this.api.delete<ApiResponse<Files>>({
        route: `api/registration/files-data/${this.fileToDelete.id}/`,
        params: { register_uuid: routeId },
        token: true,
      });

      this.toast.show('info', 'Sucesso', 'Arquivo removido');
      await this.getFiles();
      await this.setAttachments();
      this.closeDeleteModal();
    } catch (error) {
      this.toast.show('error', 'Erro', error.error.message);
    }
  }

  getDeleteModalTitle() {
    return 'Tem certeza?';
  }

  getDeleteModalMessage() {
    return 'Você não poderá reverter isso!';
  }

  getDeleteConfirmText() {
    return 'Sim, apague!';
  }

  getDeleteCancelText() {
    return 'Cancelar';
  }

  async sendAttachment(payload, routeId: string) {
    try {
      await this.api.post<ApiResponse<Files>>({
        route: 'api/registration/files-data/',
        params: { register_uuid: routeId },
        token: true,
        body: payload,
        noDefautHeaders: true,
      });
    } catch (error) {
      this.toast.show('error', 'Erro', error.error.message);
    }
  }

  async sendAttachments(_attachment: Attachment) {
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        this.toast.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota'
        );
        return;
      }

      const file_payload = _attachment.files.map((_file) => {
        const formData = new FormData();

        formData.append('register', routeId);
        formData.append('type', String(_attachment.id));
        formData.append('file', _file.file);
        formData.append('end_date_due', _attachment.end_date_due);
        formData.append('date_due', _attachment.date_due);

        return formData;
      });

      await Promise.all(
        file_payload.map((payload) => this.sendAttachment(payload, routeId))
      );
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        `Erro ao enviar arquivo ${_attachment.name}`
      );
    }
  }

  async sendRepresentativeAttachments(_attachments: RepresentativeAttachment) {
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        this.toast.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota'
        );
        return;
      }

      const requests = _attachments.attachments
        .filter((_attachment) => _attachment.files.length > 0)
        .map(async (_attachment) => {
          const file_payload = _attachment.files.map((_file) => {
            const formData = new FormData();

            formData.append('register', routeId);
            formData.append('type', String(_attachment.id));
            formData.append('file', _file.file);
            formData.append('end_date_due', _attachment.end_date_due);
            formData.append('date_due', _attachment.date_due);

            formData.append(
              'referent_id',
              String(_attachments.representative.id)
            );
            formData.append('referent', 'representative');

            return formData;
          });

          await Promise.all(
            file_payload.map((payload) => this.sendAttachment(payload, routeId))
          );
        });

      await Promise.all(requests);
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        `Erro ao enviar arquivos de representante ${_attachments.representative.name}`
      );
    }
  }

  async sendData() {
    this.sending = true;
    try {
      const registerAttachmentsRequests = this.attachments
        .filter((_attachments) => _attachments.files.length > 0)
        .map((_attachments) => this.sendAttachments(_attachments));

      const representativesAttachmentsRequests =
        this.representativeAttachments.map((_representative) =>
          this.sendRepresentativeAttachments(_representative)
        );

      await Promise.all([
        ...registerAttachmentsRequests,
        ...representativesAttachmentsRequests,
      ]);

      this.toast.show('info', 'Sucesso', 'Arquivos enviados com sucesso!');

      this.attachments = [];
      this.representativeAttachments = [];

      // Recarrega os dados: files, representatives e required files
      await Promise.all([
        this.getFiles(),
        this.getRepresentatives(),
        this.getRequiredFiles(),
      ]);
      this.setAttachments();
    } catch (error) {
      console.error(error);
    }
    this.sending = false;
  }

  validateSendedFiles() {
    let filesAux: SendedValues[] = [{ referent: 'register', slugs: [] }];

    let requiredSlugs = this.attachments
      .filter((attachment) => attachment.required)
      .map((attachment) => attachment.slug);

    let representativesRequiredSlugs = [];

    if (this.representativeAttachments.length > 0) {
      representativesRequiredSlugs =
        this.representativeAttachments[0].attachments
          .filter((attachment) => attachment.required)
          .map((attachment) => attachment.slug);
    }

    if (this.representativeAttachments.length > 0) {
      this.representativeAttachments.forEach((representative) => {
        filesAux.push({
          referent: 'representative',
          slugs: [],
          id: representative.representative.id,
        });
      });
    }

    this.files.forEach((file) => {
      if (file.type !== null) {
        if (file.referent === 'representative') {
          const index = filesAux.findIndex((f) => f.id === file.referent_id);
          filesAux[index].slugs.push(file.type.slug);
        } else {
          filesAux[0].slugs.push(file.type.slug);
        }
      }
    });

    const filesIsValid = filesAux.map((file) => {
      if (file.referent === 'register') {
        return requiredSlugs.every((slug) => file.slugs.includes(slug));
      } else {
        return representativesRequiredSlugs.every((slug) =>
          file.slugs.includes(slug)
        );
      }
    });

    return filesIsValid.every((isValid) => isValid);
  }

  // Status geral dos attachments do titular
  getOverallAttachmentStatus(): {
    status: 'complete' | 'incomplete';
    missing: number;
  } {
    let totalMissing = 0;

    this.attachments.forEach((attachment) => {
      if (attachment.required) {
        const status = this.getAttachmentStatus(attachment);
        if (status.status === 'incomplete') {
          totalMissing += status.missing;
        }
      }
    });

    return {
      status: totalMissing === 0 ? 'complete' : 'incomplete',
      missing: totalMissing,
    };
  }

  // Status geral dos attachments de um representante
  getOverallRepresentativeAttachmentStatus(
    representativeAttachment: RepresentativeAttachment
  ): { status: 'complete' | 'incomplete'; missing: number } {
    let totalMissing = 0;

    representativeAttachment.attachments.forEach((attachment) => {
      if (attachment.required) {
        const status = this.getRepresentativeAttachmentStatus(
          attachment,
          representativeAttachment.representative.id
        );
        if (status.status === 'incomplete') {
          totalMissing += status.missing;
        }
      }
    });

    return {
      status: totalMissing === 0 ? 'complete' : 'incomplete',
      missing: totalMissing,
    };
  }

  // Badge de status
  getAttachmentStatus(attachment: Attachment): {
    status: 'complete' | 'incomplete';
    missing: number;
  } {
    const files = this.getSendedFiles(attachment.id);
    const approvedFiles = files.filter((f) => f.situation === 1).length;

    // Se o anexo é obrigatório, precisa ter pelo menos 1 arquivo aprovado
    if (attachment.required) {
      const missing = approvedFiles === 0 ? 1 : 0;
      return {
        status: missing === 0 ? 'complete' : 'incomplete',
        missing: missing,
      };
    }

    // Se não é obrigatório, sempre está completo
    return {
      status: 'complete',
      missing: 0,
    };
  }

  getRepresentativeAttachmentStatus(
    attachment: Attachment,
    representativeId: number
  ): { status: 'complete' | 'incomplete'; missing: number } {
    const files = this.getRepresentativeSendedFiles(
      attachment.id,
      representativeId
    );
    const approvedFiles = files.filter((f) => f.situation === 1).length;

    // Se o anexo é obrigatório, precisa ter pelo menos 1 arquivo aprovado
    if (attachment.required) {
      const missing = approvedFiles === 0 ? 1 : 0;
      return {
        status: missing === 0 ? 'complete' : 'incomplete',
        missing: missing,
      };
    }

    // Se não é obrigatório, sempre está completo
    return {
      status: 'complete',
      missing: 0,
    };
  }

  // Validação de data
  isDateValid(attachment: Attachment): boolean {
    if (!attachment.have_due_date) {
      return true;
    }

    const fromDate = attachment.date_due;
    const toDate = attachment.end_date_due;

    if (!fromDate || !toDate) {
      return false;
    }

    // Validação básica de formato (aaaa-mm-dd)
    if (fromDate.length !== 10 || toDate.length !== 10) {
      return false;
    }

    // Verificar se as datas são válidas
    const fromDateObj = new Date(fromDate);
    const toDateObj = new Date(toDate);

    if (isNaN(fromDateObj.getTime()) || isNaN(toDateObj.getTime())) {
      return false;
    }

    // Verificar se data inicial não é maior que data final
    if (fromDateObj > toDateObj) {
      return false;
    }

    return !attachment.isFromInvalid && !attachment.isToInvalid;
  }

  // Upload de anexo (automático)
  async uploadAttachment(attachment: Attachment) {
    if (attachment.files.length === 0) {
      return;
    }

    // Verificar se precisa de data e se está válida
    if (attachment.have_due_date && !this.isDateValid(attachment)) {
      this.toast.show(
        'warning',
        'Atenção',
        'Preencha a data de validade corretamente antes de anexar o arquivo'
      );
      return;
    }

    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId) {
      this.toast.show('error', 'Erro', 'Register UUID não encontrado na rota');
      return;
    }

    this.loadingContent = true;
    try {
      await this.sendAttachments(attachment);
      this.toast.show('info', 'Sucesso', 'Arquivo anexado com sucesso!');

      // Recarregar dados e atualizar status
      await this.getFiles();
      await this.setAttachments();

      // Atualizar status da etapa após upload
      this.signupService.setPersonFormStatus(
        'attachments',
        this.validateSendedFiles()
      );
    } catch (error: any) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao anexar arquivo'
      );
    } finally {
      this.loadingContent = false;
    }
  }

  // Upload de anexo de representante (automático)
  async uploadRepresentativeAttachment(
    representativeAttachment: RepresentativeAttachment,
    fileIndex: number
  ) {
    const attachment = representativeAttachment.attachments[fileIndex];

    if (attachment.files.length === 0) {
      return;
    }

    // Verificar se precisa de data e se está válida
    if (attachment.have_due_date && !this.isDateValid(attachment)) {
      this.toast.show(
        'warning',
        'Atenção',
        'Preencha a data de validade corretamente antes de anexar o arquivo'
      );
      return;
    }

    this.loadingContent = true;
    try {
      // Criar um RepresentativeAttachment temporário apenas com o attachment atual
      const tempRepresentativeAttachment: RepresentativeAttachment = {
        representative: representativeAttachment.representative,
        attachments: [attachment],
      };

      await this.sendRepresentativeAttachments(tempRepresentativeAttachment);
      this.toast.show('info', 'Sucesso', 'Arquivo anexado com sucesso!');

      // Recarregar dados e atualizar status
      await this.getFiles();
      await this.setAttachments();

      // Atualizar status da etapa após upload
      this.signupService.setPersonFormStatus(
        'attachments',
        this.validateSendedFiles()
      );
    } catch (error: any) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao anexar arquivo'
      );
    } finally {
      this.loadingContent = false;
    }
  }
}
